import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom'; // <-- REMOVED useNavigate
import api from './api';
import { 
  Container, Box, Typography, List, ListItem, ListItemText, 
  IconButton, Button, CircularProgress, 
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField,
  Paper, Snackbar, Alert 
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn'; 
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'; 
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary'; 
import PaymentModal from './PaymentModal'; 
import PhotoUploader from './PhotoUploader'; 
import PhotoGallery from './PhotoGallery'; 

// Import Icons
import LockIcon from '@mui/icons-material/Lock';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LoginIcon from '@mui/icons-material/Login';

function MySubmissionsPage() {
  const [displays, setDisplays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [referralCodes, setReferralCodes] = useState([]);

  // Auth State
  const [isLoggedIn, setIsLoggedIn] = useState(true); 

  // Modals
  const [editOpen, setEditOpen] = useState(false);
  const [currentDisplay, setCurrentDisplay] = useState(null);
  const [newAddress, setNewAddress] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [displayToDelete, setDisplayToDelete] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [displayToPay, setDisplayToPay] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [displayForUpload, setDisplayForUpload] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedDisplay, setSelectedDisplay] = useState(null);
  const [user, setUser] = useState(null); // New state for user info
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileData, setProfileData] = useState({ firstName: '', surName: '', email: '' });
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('success'); // 'success' or 'error'

  // SEO
  useEffect(() => {
    document.title = "My Submissions | Christmas Lights Map";
  }, []);

  // --- FETCH LOGIC ---
const fetchAllData = async () => {
  try {
    setLoading(true);
    setError('');
    // Added /users/profile to the Promise.all
    const [displaysRes, codesRes, userRes] = await Promise.all([
      api.get('/displays/me'),
      api.get('/referral-codes'),
      api.get('/users/profile') 
    ]);
    setDisplays(displaysRes.data);
    setReferralCodes(codesRes.data);
    setUser(userRes.data); // Store the user data
  } catch (err) {
    if (err.response && err.response.status === 401) {
        setIsLoggedIn(false);
    } else {
        setError('Failed to fetch your data.');
    }
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoggedIn(false);
      setLoading(false);
    } else {
      fetchAllData();
    }
  }, []); 

  // --- Handlers ---
const handleProfileEditClick = () => {
  setProfileData({
    firstName: user.first_name,
    surName: user.sur_name,
    email: user.email
  });
  setProfileOpen(true);
};

const handleProfileSave = async () => {
  try {
    const res = await api.put('/users/profile', profileData);
    setUser(res.data.user);
    setProfileOpen(false);
    
    // Trigger the success toast
    setToastMessage('Profile updated successfully!');
    setToastSeverity('success');
    setToastOpen(true);
  } catch (err) {
    // Trigger an error toast
    setToastMessage(err.response?.data?.message || 'Failed to update profile');
    setToastSeverity('error');
    setToastOpen(true);
  }
};

  const handleEditClick = (display) => {
    setCurrentDisplay(display);
    setNewAddress(display.address);
    setEditOpen(true);
  };
  const handleEditClose = () => { setEditOpen(false); };
const handleEditSave = async () => {
  try {
    await api.put(`/displays/${currentDisplay.id}`, { address: newAddress }); 
    handleEditClose();
    fetchAllData(); 
    // Success Toast
    setToastMessage('Display address updated!');
    setToastSeverity('success');
    setToastOpen(true);
  } catch (err) {
    setToastMessage('Failed to update display.');
    setToastSeverity('error');
    setToastOpen(true);
  }
};

  const handleDeleteClick = (display) => {
    setDisplayToDelete(display);
    setDeleteOpen(true);
  };
  const handleDeleteClose = () => { setDeleteOpen(false); };
  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/displays/${displayToDelete.id}`); 
      handleDeleteClose();
      setDisplays(displays.filter(d => d.id !== displayToDelete.id));
    } catch (err) {
      setError('Failed to delete display.');
    }
  };

  const handlePayClick = (display) => {
    setDisplayToPay(display);
    setPaymentModalOpen(true);
  };
  const handlePaymentClose = () => { setPaymentModalOpen(false); };
  const handlePaymentSuccess = (paidDisplayId) => {
    setDisplays(displays.map(d => 
      d.id === paidDisplayId ? { ...d, payment_status: 'completed' } : d
    ));
    fetchAllData();
  };

  const handleUploadClick = (display) => {
    setDisplayForUpload(display);
    setUploadModalOpen(true);
  };
  const handleUploadClose = () => { setUploadModalOpen(false); };

  const handleOpenGallery = (display) => {
    setSelectedDisplay(display);
    setGalleryOpen(true);
  };
  const handleCloseGallery = () => { setGalleryOpen(false); };

  const handlePhotoDeleted = (deletedPhotoId) => {
    setDisplays(prevDisplays => 
      prevDisplays.map(display => {
        if (display.id === selectedDisplay.id) {
          return {
            ...display,
            photos: display.photos.filter(p => p.id !== deletedPhotoId)
          };
        }
        return display;
      })
    );
    setSelectedDisplay(prev => ({
      ...prev,
      photos: prev.photos.filter(p => p.id !== deletedPhotoId)
    }));
    setToastMessage('Photo removed successfully.');
    setToastSeverity('success');
    setToastOpen(true);
  };

  // --- ADD THIS DELETE ACCOUNT LOGIC HERE ---
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

const handleDeleteAccount = async () => {
  try {
    await api.delete('/users/me'); // Corrected path for your api.js config
    
    localStorage.removeItem('token');
    
    // Instead of going home, go to the Goodbye page
    window.location.href = '/goodbye'; 
  } catch (err) {
    console.error("Error deleting account:", err);
    setError('Failed to delete account. Please contact support.');
  }
};

const handleDeleteCode = async (codeId) => {
  try {
    await api.delete(`/discount-codes/${codeId}`); 
    setReferralCodes(referralCodes.filter(c => c.id !== codeId));
    setToastMessage('Referral code deleted.');
    setToastSeverity('success');
    setToastOpen(true);
  } catch (err) {
    setToastMessage('Failed to delete code.');
    setToastSeverity('error');
    setToastOpen(true);
  }
};

  // --- RENDER "NOT LOGGED IN" VIEW ---
  if (!isLoggedIn) {
    return (
      <Container component="main" maxWidth="sm">
        <Paper elevation={3} sx={{ mt: 8, p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <LockIcon color="secondary" sx={{ fontSize: 60, mb: 2 }} />
          <Typography component="h1" variant="h4" gutterBottom align="center">
            Log In Required
          </Typography>
          <Typography variant="body1" align="center" sx={{ mb: 4 }}>
            Please log in to view your submissions, edit your displays, or check your referral codes.
          </Typography>
          
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<LoginIcon />}
            component={RouterLink} 
            to="/login"
            sx={{ mb: 2 }}
          >
            Login
          </Button>
          
          <Button
            fullWidth
            variant="outlined"
            size="large"
            startIcon={<PersonAddIcon />}
            component={RouterLink} 
            to="/register"
          >
            Create Account
          </Button>
        </Paper>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ marginTop: 8 }}>
        
{user && (
  <Paper 
    variant="outlined" 
    sx={{ 
      p: 2, 
      mb: 4, 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      bgcolor: '#f8f9fa', 
      borderLeft: '5px solid #2e7d32' 
    }}
  >
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
        Account Info
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {user.first_name} {user.sur_name} ({user.username})
      </Typography>
      <Typography variant="body2" color="text.secondary">{user.email}</Typography>
    </Box>
    <Button 
      variant="outlined" 
      size="small" 
      startIcon={<EditIcon />} 
      onClick={handleProfileEditClick}
    >
      Edit Profile
    </Button>
  </Paper>
)}

        {/* REFERRAL CODES */}
        <Box sx={{ mb: 4, p: 2, backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            My Referral Codes
          </Typography>
          {referralCodes.length === 0 ? (
            <Typography>You have not earned any referral codes yet. Make a new submission to get one!</Typography>
          ) : (
            <List>
              {referralCodes.map((code) => (
                <ListItem 
                  key={code.id} 
                  divider
                  secondaryAction={
                    code.current_uses === 0 && (
                      <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteCode(code.id)}>
                        <DeleteIcon />
                      </IconButton>
                    )
                  }
                >
                  <ListItemText 
                    primaryTypographyProps={{ fontWeight: 'bold', fontSize: '1.1rem' }}
                    primary={`Code: ${code.code}`}
                    secondary={code.current_uses > 0 ? 'Status: Used' : 'Status: Unused (Share with a friend!)'}
                    secondaryTypographyProps={{
                      color: code.current_uses > 0 ? 'textSecondary' : 'primary'
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* SUBMISSIONS */}
        <Typography variant="h4" component="h1" gutterBottom>
          My Submissions
        </Typography>
        {error && <Typography color="error">{error}</Typography>}
        <List>
          {displays.length === 0 ? (
            <Typography>You have not made any submissions yet.</Typography>
          ) : (
            displays.map((display) => (
              <ListItem 
                key={display.id} 
                divider
                secondaryAction={
                  <>
                    {display.payment_status === 'pending' && (
                      <IconButton edge="end" aria-label="pay" onClick={() => handlePayClick(display)}>
                        <MonetizationOnIcon />
                      </IconButton>
                    )}
                    
                    {display.payment_status === 'completed' && (
                      <>
                        {display.photos && display.photos.length > 0 && (
                          <IconButton edge="end" aria-label="view photos" onClick={() => handleOpenGallery(display)}>
                            <PhotoLibraryIcon />
                          </IconButton>
                        )}
                        <IconButton edge="end" aria-label="upload photos" onClick={() => handleUploadClick(display)} sx={{ ml: 1 }}>
                          <PhotoCameraIcon />
                        </IconButton>
                      </>
                    )}
                    
                    <IconButton edge="end" aria-label="edit" onClick={() => handleEditClick(display)} sx={{ ml: 1 }}>
                      <EditIcon />
                    </IconButton>
                    
                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteClick(display)} sx={{ ml: 1 }}>
                      <DeleteIcon />
                    </IconButton>
                  </>
                }
              >
                <ListItemText 
                  primary={display.address} 
                  secondary={`Status: ${display.payment_status}`} 
                  secondaryTypographyProps={{ 
                    color: display.payment_status === 'pending' ? 'textSecondary' : 'primary' 
                  }}
                />
              </ListItem>
            ))
          )}
        </List>
      </Box>

      {/* MODALS */}
<Dialog open={profileOpen} onClose={() => setProfileOpen(false)} maxWidth="sm" fullWidth>
  <DialogTitle>Edit Your Profile</DialogTitle>
  <DialogContent>
    <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="First Name"
        fullWidth
        variant="outlined"
        value={profileData.firstName}
        onChange={(e) => setProfileData({...profileData, firstName: e.target.value})}
      />
      <TextField
        label="Surname"
        fullWidth
        variant="outlined"
        value={profileData.surName}
        onChange={(e) => setProfileData({...profileData, surName: e.target.value})}
      />
      <TextField
        label="Email Address"
        fullWidth
        variant="outlined"
        type="email"
        value={profileData.email}
        onChange={(e) => setProfileData({...profileData, email: e.target.value})}
      />
    </Box>
  </DialogContent>
  <DialogActions sx={{ p: 2 }}>
    <Button onClick={() => setProfileOpen(false)}>Cancel</Button>
    <Button variant="contained" onClick={handleProfileSave} color="primary">
      Save Changes
    </Button>
  </DialogActions>
</Dialog>

      <Dialog open={editOpen} onClose={handleEditClose}>
        <DialogTitle>Edit Address</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Update the address for your display. Re-geocoding will be performed.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="address"
            label="Address"
            type="text"
            fullWidth
            variant="standard"
            value={newAddress}
            onChange={(e) => setNewAddress(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button onClick={handleEditSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={handleDeleteClose}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this submission? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {displayToPay && (
        <PaymentModal
          open={paymentModalOpen}
          onClose={handlePaymentClose}
          display={displayToPay}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}

      <Dialog open={uploadModalOpen} onClose={handleUploadClose} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Photos for {displayForUpload?.address}</DialogTitle>
        <DialogContent>
          {displayForUpload && (
            <PhotoUploader displayId={displayForUpload.id} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {selectedDisplay && (
        <PhotoGallery
          open={galleryOpen}
          onClose={handleCloseGallery}
          photos={selectedDisplay.photos || []} 
          displayName={selectedDisplay.address}
          onPhotoDeleted={handlePhotoDeleted} 
        />
      )}
      
{/* --- ADD DANGER ZONE AT THE BOTTOM --- */}
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper variant="outlined" sx={{ p: 3, borderColor: '#ffcdd2', bgcolor: '#fffbfa' }}>
          <Typography variant="h6" color="error" gutterBottom>Danger Zone</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Permanently delete your account and all associated light displays.
          </Typography>
          <Button 
            variant="outlined" 
            color="error" 
            startIcon={<DeleteForeverIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete My Account
          </Button>
        </Paper>
      </Box>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ color: 'error.main' }}>Permanently Delete Account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure? This will instantly remove your submissions from the map and you will not be able to log back in.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined">Cancel</Button>
          <Button onClick={handleDeleteAccount} variant="contained" color="error">
            Yes, Delete Everything
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar 
  open={toastOpen} 
  autoHideDuration={4000} 
  onClose={() => setToastOpen(false)}
  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
>
  <Alert 
    onClose={() => setToastOpen(false)} 
    severity={toastSeverity} 
    sx={{ width: '100%' }}
    variant="filled"
  >
    {toastMessage}
  </Alert>
</Snackbar>

    </Container>
  );
}

export default MySubmissionsPage;